import { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Plus,
  Bell,
  X,
  Pencil,
  SearchIcon,
  Settings,
  RotateCw,
  FilterX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Define default columns for settings
const defaultColumns = [
  { id: "date", label: "Date", required: true },
  { id: "poId", label: "PO ID", required: true },
  { id: "supplier", label: "Supplier", required: true },
  { id: "design", label: "Design", required: false },
  { id: "expectedDate", label: "Expected Date", required: false },
];

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "supplierAZ", label: "Supplier (A-Z)" },
  { value: "supplierZA", label: "Supplier (Z-A)" },
];

const PurchaseOrder = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [selectedOrderForReminder, setSelectedOrderForReminder] =
    useState(null);
  const [reminderDateTime, setReminderDateTime] = useState(null);
  const [reminderDescription, setReminderDescription] = useState("");
  const navigate = useNavigate();

  // New states for search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
  );
  const [filters, setFilters] = useState({
    dateRange: "",
    sortBy: "",
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (column?.required) return;

    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate refresh
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      dateRange: "",
      sortBy: "",
    });
  };

  const handleReminderClick = (e, order) => {
    e.stopPropagation();
    setSelectedOrderForReminder(order);
    setReminderModalOpen(true);
  };

  const handleSetReminder = () => {
    console.log("Setting reminder for order:", selectedOrderForReminder?.id);
    console.log("Reminder time:", reminderDateTime);
    console.log("Description:", reminderDescription);
    setReminderModalOpen(false);
    setSelectedOrderForReminder(null);
    setReminderDateTime(null);
    setReminderDescription("");
  };

  // Sample data for demonstration
  const orders = [
    {
      id: "PO-3",
      supplier: "Prints Bank",
      design: "Modern Print",
      expectedDate: "16 May, 2025",
      date: "15",
      month: "MAY",
      status: "pending",
    },
    {
      id: "PO-1",
      supplier: "Supplier 1",
      design: "Classic Design",
      expectedDate: "-",
      date: "15",
      month: "MAY",
      status: "pending",
    },
    {
      id: "PO-2",
      supplier: "Prints Bank",
      design: "Eco Style",
      expectedDate: "18 May, 2025",
      date: "16",
      month: "MAY",
      status: "pending",
    },
    {
      id: "PO-4",
      supplier: "Supplier 2",
      design: "Minimalist",
      expectedDate: "20 May, 2025",
      date: "17",
      month: "MAY",
      status: "pending",
    },
    // Completed Orders
    {
      id: "PO-C1",
      supplier: "Fashion Fabrics",
      design: "Summer Collection",
      expectedDate: "10 May, 2025",
      date: "10",
      month: "MAY",
      status: "completed",
    },
    {
      id: "PO-C2",
      supplier: "Textile Hub",
      design: "Winter Basics",
      expectedDate: "12 May, 2025",
      date: "12",
      month: "MAY",
      status: "completed",
    },
    {
      id: "PO-C3",
      supplier: "Prints Bank",
      design: "Festival Edition",
      expectedDate: "8 May, 2025",
      date: "08",
      month: "MAY",
      status: "completed",
    },
    {
      id: "PO-C4",
      supplier: "Global Textiles",
      design: "Premium Cotton Series",
      expectedDate: "5 May, 2025",
      date: "05",
      month: "MAY",
      status: "completed",
    },
  ];

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Purchase Orders
          </h1>
          <button
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium 
                     hover:bg-[#4A47A3] transition-all duration-200 shadow-sm 
                     hover:shadow-md active:transform active:scale-95 
                     flex items-center gap-2"
            onClick={() => navigate("/purchase/orders/create")}>
            <Plus size={16} className="stroke-[2]" />
            Create Purchase Order
          </button>
        </div>

        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[450px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by PO ID, Supplier, Design..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600 bg-white
                         hover:border-gray-300 focus:shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 
                           hover:bg-gray-100 p-1.5 rounded-full transition-all duration-200">
                  <X size={14} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100 transition-all duration-200"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={18}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100 transition-all duration-200"
              title="Column Settings">
              <Settings size={18} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="px-6 py-4 flex items-center justify-between">
        {/* Tabs on the left */}
        <div>
          <div className="flex items-center gap-1 bg-gray-100/80 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab("pending")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                       ${
                         activeTab === "pending"
                           ? "bg-white text-[#5652B0] shadow-sm ring-1 ring-black/5"
                           : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                       }`}>
              Pending Orders
            </button>
            <button
              onClick={() => setActiveTab("completed")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                       ${
                         activeTab === "completed"
                           ? "bg-white text-[#5652B0] shadow-sm ring-1 ring-black/5"
                           : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                       }`}>
              Completed Orders
            </button>
          </div>
        </div>

        {/* Filters on the right */}
        <div className="flex items-center gap-3">
          {/* Date Range Filter */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                  borderWidth: "1px",
                },
              },
            }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={filters.dateRange}
              label="Date Range"
              onChange={e =>
                setFilters({ ...filters, dateRange: e.target.value })
              }>
              <MenuItem value="">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="last30days">Last 30 Days</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
            </Select>
          </FormControl>

          {/* Sort By Filter */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                  borderWidth: "1px",
                },
              },
            }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={filters.sortBy}
              label="Sort By"
              onChange={e =>
                setFilters({ ...filters, sortBy: e.target.value })
              }>
              {sortOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          {(searchQuery ||
            Object.values(filters).some(value => value !== "")) && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700
                     bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200
                     border border-gray-200 hover:border-gray-300 active:bg-gray-200">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseSettings}
        PaperProps={{
          className: "mt-2",
          elevation: 0,
          sx: {
            border: "1px solid #E5E7EB",
            borderRadius: "0.5rem",
            minWidth: 180,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-700">Show/Hide Columns</p>
        </div>
        {defaultColumns
          .filter(column => {
            // Only show expectedDate option in pending tab
            if (column.id === "expectedDate" && activeTab !== "pending") {
              return false;
            }
            return true;
          })
          .map(column => (
            <MenuItem
              key={column.id}
              onClick={() => handleColumnToggle(column.id)}
              disabled={column.required}
              sx={{ fontSize: "0.875rem" }}>
              <Checkbox
                checked={visibleColumns[column.id]}
                disabled={column.required}
                size="small"
                sx={{
                  color: "#5652B0",
                  "&.Mui-checked": {
                    color: "#5652B0",
                  },
                }}
              />
              {column.label}
            </MenuItem>
          ))}
      </Menu>

      {/* Existing Table Component */}
      <div className="px-6">
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid #E5E7EB",
            borderRadius: "0.5rem",
          }}>
          <TableContainer
            sx={{ maxHeight: "calc(100vh - 300px)" }}
            className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {defaultColumns
                    .filter(column => {
                      // Only show expectedDate column in pending tab
                      if (
                        column.id === "expectedDate" &&
                        activeTab !== "pending"
                      ) {
                        return false;
                      }
                      return visibleColumns[column.id];
                    })
                    .map(column => (
                      <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          minWidth: column.id === "design" ? "200px" : "120px",
                          backgroundColor: "#F9FAFB",
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}
                        className="font-outfit">
                        {column.label}
                      </TableCell>
                    ))}
                  <TableCell
                    align="center"
                    sx={{
                      minWidth: "120px",
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders
                  .filter(o => o.status === activeTab)
                  .map(order => (
                    <TableRow
                      hover
                      key={order.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                      }}>
                      {visibleColumns.date && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          {order.date} {order.month}
                        </TableCell>
                      )}
                      {visibleColumns.poId && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                            fontWeight: 500,
                          }}
                          className="font-outfit">
                          {order.id}
                        </TableCell>
                      )}
                      {visibleColumns.supplier && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                            fontWeight: 500,
                          }}
                          className="font-outfit">
                          {order.supplier}
                        </TableCell>
                      )}
                      {visibleColumns.design && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          {order.design}
                        </TableCell>
                      )}
                      {visibleColumns.expectedDate &&
                        activeTab === "pending" && (
                          <TableCell
                            align="center"
                            sx={{
                              padding: "12px 16px",
                              fontSize: "14px",
                              color: "#4B5563",
                            }}
                            className="font-outfit">
                            {order.expectedDate}
                          </TableCell>
                        )}
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                        }}
                        className="font-outfit">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors"
                            onClick={e => {
                              e.stopPropagation();
                              navigate(
                                `/purchase/orders/create?id=${order.id}`,
                              );
                            }}>
                            <Pencil size={16} className="text-[#5652B0]" />
                          </button>
                          {activeTab === "pending" && (
                            <button
                              className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors"
                              onClick={e => handleReminderClick(e, order)}>
                              <Bell size={16} className="text-[#5652B0]" />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                {orders.filter(o => o.status === activeTab).length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        Object.values(visibleColumns).filter(Boolean).length +
                        (activeTab === "pending" ? 0 : -1) +
                        1 // +1 for Actions column
                      }
                      align="center"
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#6B7280",
                      }}
                      className="font-outfit">
                      No {activeTab} purchase orders found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Dialog
          open={reminderModalOpen}
          onClose={() => {
            setReminderModalOpen(false);
            setSelectedOrderForReminder(null);
            setReminderDateTime(null);
            setReminderDescription("");
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "12px",
            },
          }}>
          <DialogTitle className="flex justify-between items-center border-b pb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#5652B0]" />
              <span className="text-xl font-semibold text-gray-800">
                Set Reminder
              </span>
            </div>
            <button
              onClick={() => {
                setReminderModalOpen(false);
                setSelectedOrderForReminder(null);
                setReminderDateTime(null);
                setReminderDescription("");
              }}
              className="text-gray-500 hover:text-gray-700 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </DialogTitle>
          <DialogContent>
            <div className="mt-6 flex flex-col gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">Purchase Order</div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">
                    {selectedOrderForReminder?.id}
                  </span>
                  <span className="font-bold text-gray-800">
                    {selectedOrderForReminder?.supplier}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  Design:{" "}
                  <span className="text-gray-800">
                    {selectedOrderForReminder?.design}
                  </span>
                </div>
              </div>

              <DateTimePicker
                label="Reminder Date & Time"
                value={reminderDateTime}
                onChange={newValue => setReminderDateTime(newValue)}
                className="w-full"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "&:hover fieldset": {
                          borderColor: "#5652B0",
                        },
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#5652B0",
                        },
                    },
                  },
                }}
              />

              <TextField
                label="Reminder Description"
                multiline
                rows={3}
                value={reminderDescription}
                onChange={e => setReminderDescription(e.target.value)}
                placeholder="Add a note about this reminder..."
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&:hover fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#5652B0",
                    },
                }}
              />
            </div>
          </DialogContent>
          <DialogActions className="border-t p-4 gap-3">
            <Button
              onClick={() => {
                setReminderModalOpen(false);
                setSelectedOrderForReminder(null);
                setReminderDateTime(null);
                setReminderDescription("");
              }}
              variant="outlined"
              sx={{
                borderColor: "#5652B0",
                color: "#5652B0",
                "&:hover": {
                  borderColor: "#4A47A3",
                  backgroundColor: "rgba(86, 82, 176, 0.04)",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleSetReminder}
              variant="contained"
              disabled={!reminderDateTime}
              sx={{
                bgcolor: "#5652B0",
                "&:hover": {
                  bgcolor: "#4A47A3",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#4A47A3",
                  boxShadow: "none",
                },
              }}>
              Set Reminder
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </div>
  );
};

export default PurchaseOrder;
